<template>
  <div class="bg-light-soft min-vh-100">
    <Header></Header>

    <header class="hero-section position-relative overflow-hidden">
      <div class="decoration-blur"></div>
      <div class="container-lg position-relative">
        <div class="row py-20 justify-content-center text-center">
          <div class="col-lg-10 mt-5">
            <span class="badge rounded-pill bg-primary-soft text-primary mb-4 px-4 py-2 fw-bold animate-fade-in">
              Next-Gen Genomic Analysis Platform
            </span>
            <h1 class="display-3 fw-bold mb-4 gradient-text animate-up">
              Polygenic Score <br/>Computing Platform
            </h1>
            <p class="lead text-muted mb-8 px-lg-12 animate-up-delayed">
              A unified high-performance pipeline for <b>SNP Weights</b> estimation.
              Seamlessly integrate GWAS statistics with 12+ state-of-the-art PGS methods
              powered by Cromwell & S3.
            </p>

            <div class="d-flex justify-content-center gap-4 mb-10 animate-up-delayed-2">
              <router-link to="/login" class="btn btn-primary btn-lg px-9 py-4 rounded-pill shadow-lg-primary fw-bold hover-lift">
                Get Started Free
              </router-link>
              <a href="https://github.com/xinyu-c9/PGS-hub_offline" target="_blank" class="btn btn-white btn-lg px-9 py-4 rounded-pill shadow-sm fw-bold hover-lift">
                <i class="bi bi-github me-2"></i>GitHub
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="container position-relative z-index-20">
      <div class="stats-bar shadow-2xl">
        <div class="row py-8 px-4 g-4">
          <div class="col-md-4 text-center border-end-divider">
            <div class="stat-item">
              <span class="stat-number">{{ homeDatas.runnerCount || 0 }}</span>
              <p class="stat-label">Analysis Completed</p>
            </div>
          </div>
          <div class="col-md-4 text-center border-end-divider">
            <div class="stat-item">
              <span class="stat-number">{{ homeDatas.userCount || 0 }}</span>
              <p class="stat-label">Registered Researchers</p>
            </div>
          </div>
          <div class="col-md-4 text-center">
            <div class="stat-item">
              <span class="stat-number text-highlight">{{ homeDatas.runningCount || 0 }}</span>
              <p class="stat-label">Live Computing Jobs</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section class="container-lg py-24">
      <div class="text-center mb-16">
        <h6 class="text-primary fw-bold text-uppercase tracking-widest mb-3">Workflow</h6>
        <h2 class="display-5 fw-bold mb-4 text-dark-blue">Three Steps to Results</h2>
        <p class="text-muted mx-auto max-w-2xl">Simplified genomic workflow managed by Cromwell backend and S3 storage.</p>
      </div>

      <div class="row g-5 text-center">
        <div v-for="(step, index) in steps" :key="index" class="col-xl-4 col-md-6">
          <div class="card h-100 border-0 shadow-soft-hover transition-up cursor-pointer step-card" @click="toFunctional(step.action)">
            <div class="card-body p-8">
              <div class="step-badge mb-6 mx-auto">{{ index + 1 }}</div>
              <div class="icon-circle mb-6 bg-primary-light mx-auto">
                <img :src="step.icon" width="60" :alt="step.title">
              </div>
              <h4 class="fw-bold mb-4">{{ step.title }}</h4>
              <p class="text-muted mb-0 leading-relaxed">{{ step.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white py-24 border-top">
      <div class="container-lg">
        <div class="row g-8">
          <div class="col-lg-8">
            <div class="mb-10 p-5 rounded-4 bg-primary-soft border border-primary-subtle">
              <h6 class="text-primary fw-bold text-uppercase mb-3"><i class="bi bi-journal-check me-2"></i>Featured Publication</h6>
              <h5 class="fw-bold mb-3">PGS-hub: A comprehensive platform for polygenic score computing and analysis</h5>
              <p class="text-muted small mb-4">Our methodology and platform architecture have been published in <i>Nature Communications</i> (2026).</p>
              <a href="https://www.nature.com/articles/s41467-026-68599-7" target="_blank" class="btn btn-primary btn-sm rounded-pill px-4">
                Read the Paper
              </a>
            </div>

            <div class="d-flex align-items-center mb-8">
              <h3 class="fw-bold text-dark-blue mb-0">Latest News</h3>
              <div class="ms-4 flex-grow-1 border-bottom opacity-10"></div>
            </div>
            <div class="row g-4">
              <div v-for="(news, index) in newsList" :key="index" class="col-12">
                <div class="news-item d-flex gap-4 align-items-start p-4 rounded-4 hover-bg-light transition-base">
                  <div class="news-date text-center">
                    <span class="d-block h4 fw-bold mb-0 text-primary">{{ news.day }}</span>
                    <span class="small text-muted text-uppercase">{{ news.month }}</span>
                  </div>
                  <div>
                    <span class="badge bg-light text-primary mb-2 border border-primary-subtle">{{ news.category }}</span>
                    <h5 class="fw-bold mb-2">{{ news.title }}</h5>
                    <p class="text-muted small mb-0">{{ news.summary }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="bg-light rounded-4 p-6 h-100 border border-light-subtle">
              <h4 class="fw-bold text-dark-blue mb-6">Release Notes</h4>
              <div class="timeline-sm">
                <div v-for="(version, index) in versions" :key="index" class="timeline-item mb-5 pb-2 border-bottom border-white">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="badge bg-dark text-white fw-bold">v{{ version.number }}</span>
                    <small class="text-muted">{{ version.date }}</small>
                  </div>
                  <ul class="list-unstyled small text-muted mb-0">
                    <li v-for="(note, nIndex) in version.notes" :key="nIndex" class="mb-1">
                      <i class="bi bi-check2 text-success me-2"></i>{{ note }}
                    </li>
                  </ul>
                </div>
              </div>
              <a href="https://github.com/xinyu-c9/PGS-hub_offline/releases" target="_blank" class="btn btn-link text-primary p-0 small fw-bold text-decoration-none mt-3">
                Full changelog on GitHub <i class="bi bi-chevron-right"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="footer py-12 bg-dark text-white-50">
      <div class="container-lg">
        <div class="row g-8 mb-10">
          <div class="col-lg-4">
            <img src="/img/logos/PGS-hub.png" height="45" class="mb-6 brightness-0 invert" alt="Logo">
            <p class="small pe-lg-8 leading-relaxed">
              <b>PGS-hub</b> is developed and maintained by the National Genomics Data Center (NGDC), China National Center for Bioinformation (CNCB).
            </p>
            <div class="d-flex gap-3 mt-4">
              <a href="https://github.com/xinyu-c9/PGS-hub_offline" target="_blank" class="text-white-50 hover-white fs-4"><i class="bi bi-github"></i></a>
              <a href="mailto:contact@cncb.ac.cn" class="text-white-50 hover-white fs-4"><i class="bi bi-envelope-fill"></i></a>
            </div>
          </div>
          <div class="col-6 col-lg-2">
            <h6 class="text-white fw-bold mb-4">Platform</h6>
            <ul class="list-unstyled small">
              <li class="mb-2"><router-link to="/functionItem" class="text-reset text-decoration-none hover-white">Algorithms</router-link></li>
              <li class="mb-2"><router-link to="/gwasreference" class="text-reset text-decoration-none hover-white">GWAS Data</router-link></li>
              <li class="mb-2"><router-link to="/statistics" class="text-reset text-decoration-none hover-white">Job Stats</router-link></li>
            </ul>
          </div>
          <div class="col-6 col-lg-2">
            <h6 class="text-white fw-bold mb-4">Links</h6>
            <ul class="list-unstyled small">
              <li class="mb-2"><a href="https://www.nature.com/articles/s41467-026-68599-7" target="_blank" class="text-reset text-decoration-none hover-white">Nature Article</a></li>
              <li class="mb-2"><a href="https://github.com/xinyu-c9/PGS-hub_offline" target="_blank" class="text-reset text-decoration-none hover-white">Source Code</a></li>
            </ul>
          </div>
          <div class="col-lg-4">
            <h6 class="text-white fw-bold mb-4">Contact</h6>
            <p class="small mb-2"><i class="bi bi-geo-alt-fill me-2 text-primary"></i>Beijing, China, 100101</p>
            <p class="small mb-4"><i class="bi bi-telephone-fill me-2 text-primary"></i>86-10-84097216</p>
            <div class="bg-secondary-dark p-3 rounded small border border-white-10">
              <i class="bi bi-shield-lock-fill me-2"></i>Secured by CNCB Infrastructure
            </div>
          </div>
        </div>

        <div class="pt-8 border-top border-white-10 text-center">
          <p class="small mb-2">版权所有 © <b>国家生物信息中心</b> 2026</p>
          <p class="x-small opacity-50 mb-0">京ICP备 10050270号-13 | Powered by Cromwell</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import Header from "@/components/commons/HeaderItem.vue"
import { Account } from "@/api"
import pb from "@/utils/pocketbase"

export default {
  name: "HomeItem",
  components: { Header },
  data() {
    return {
      homeDatas: { runnerCount: 0, userCount: 0, runningCount: 0 },

      // 这里是 PB 的配置：按你实际部署改
      // 示例：VUE_APP_PB_URL="http://192.168.88.66:8090"
      PB_BASE: (process.env.VUE_APP_PB_URL || "").replace(/\/+$/, ""),
      RUNS_COLLECTION: process.env.VUE_APP_PB_RUNS || "runs",
      USERS_COLLECTION: process.env.VUE_APP_PB_USERS || "users", // 你的 Auth collection 通常就叫 users

      steps: [
        {
          title: "Upload GWAS",
          desc: "Securely upload your summary statistics. Automated QC check included.",
          icon: require("@/assets/up-cloud.png"),
          action: "GWAS"
        },
        {
          title: "Choose Methods",
          desc: "Select algorithms and references. Cromwell handles the heavy lifting.",
          icon: require("@/assets/impute.png"),
          action: "PRS"
        },
        {
          title: "Export Weights",
          desc: "Download weights and visualized reports via CNCB secure storage.",
          icon: require("@/assets/down-cloud.png"),
          action: "jobs"
        }
      ],
      newsList: [
        { day: "22", month: "Feb", category: "Publication", title: "Nature Communications Publication", summary: "Our study on PGS scalability is now online." },
        { day: "10", month: "Feb", category: "Update", title: "Added X-Wing Support", summary: "Support for cross-ancestry estimation." }
      ],
      versions: [
        { number: "2.5.1", date: "2026-02-21", notes: ["Nature article links", "CNCB footer"] }
      ]
    }
  },
  methods: {
    // ---- PB helper：拿 collection 的 totalItems（PB 标准 REST 返回里就有 totalItems）----
    async pbCount(collection, { filter = "" } = {}) {
      if (!this.PB_BASE) {
        // 没配 PB_BASE 就直接返回 0（避免首页报错）
        return 0;
      }

      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("perPage", "1");
      if (filter) params.set("filter", filter);

      const url = `${this.PB_BASE}/api/collections/${encodeURIComponent(collection)}/records?${params.toString()}`;

      // 如果你用 PB AuthStore（cookie/token），这里加上 credentials 比较稳
      const resp = await fetch(url, { method: "GET", credentials: "include" });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        throw new Error(`PB count failed: ${resp.status} ${resp.statusText} | ${url} | ${txt}`);
      }
      const json = await resp.json();
      return Number(json.totalItems || 0);
    },

    // 获取统计数据（直接连 PB，不额外加 api 文件）
    async initDatas() {
  try {
    const base = (pb.baseUrl || "").replace(/\/+$/, ""); // pb 实例自带 baseUrl
    const resp = await fetch(`${base}/api/public/home/datas`, {
      method: "GET",
      credentials: "omit",
    });
    const json = await resp.json();
    if (json && json.code === 0) {
      this.homeDatas = json.data;
    }
  } catch (err) {
    console.error("Failed to load public home datas:", err);
  }
},



    // 处理导航点击
    async toFunctional(val) {
      // 1. 检查是否登录 (统一使用 PB 状态)
      if (Account.isAuthenticated()) {
        const routeNameMap = {
          'GWAS': 'gwasreference',
          'PRS': 'functionItem',
          'jobs': 'statistics'
        };
        this.$router.push({ name: routeNameMap[val] || 'home' });
      } else {
        // 未登录跳转登录，并记录回跳地址
        this.$router.push({
          name: 'login',
          query: { redirect: this.$router.currentRoute.fullPath }
        });
      }
    }
  },
  mounted() {
    this.initDatas();
  }
}
</script>

<style scoped>
/* 保持原有美学样式不变 */
.bg-light-soft { background-color: #f8faff; background-image: radial-gradient(#e5e7eb 1px, transparent 1px); background-size: 40px 40px; }
.gradient-text { background: linear-gradient(135deg, #1a2a6c 0%, #4f46e5 50%, #796CFF 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; }
.decoration-blur { position: absolute; width: 600px; height: 600px; background: radial-gradient(circle, rgba(121, 108, 255, 0.08) 0%, transparent 70%); top: -150px; right: -150px; z-index: 0; }
.animate-up { animation: fadeInUp 0.8s ease-out forwards; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
.stats-bar { background: linear-gradient(135deg, #9777c0 0%, #2a4099 100%); border-radius: 2rem; color: white; margin-top: -4rem; padding: 1.5rem; border: 4px solid rgba(255, 255, 255, 0.1); }
.stat-number { font-size: 3.5rem; font-weight: 850; background: linear-gradient(180deg, #fff 30%, rgba(255,255,255,0.6)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.text-highlight { color: #00f2fe; }
.step-card { transition: all 0.3s ease; background: white; border-radius: 1.5rem; }
.step-card:hover { transform: translateY(-10px); box-shadow: 0 20px 40px rgba(121, 108, 255, 0.12) !important; }
.step-badge { width: 40px; height: 40px; background: #796CFF; color: white; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: bold; }
.icon-circle { width: 100px; height: 100px; background: #f0f2ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.news-item:hover { background-color: #f8faff; transform: translateX(5px); }
.bg-secondary-dark { background-color: rgba(255, 255, 255, 0.05) !important; }
.footer { border-top: 1px solid rgba(255, 255, 255, 0.1); }
.py-24 { padding: 6rem 0; }
.py-12 { padding: 3rem 0; }
.shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); }
.z-index-20 { z-index: 20; }
.bg-primary-soft { background-color: rgba(121, 108, 255, 0.08); }
.hover-lift:hover { transform: translateY(-3px); }
</style>